import { useMemo, useRef, useState } from 'react'
import type { PointerEvent as ReactPointerEvent } from 'react'
import type { Diagnosis } from '../types'
import { IconInfo, IconSpinner } from '../icons'
import { marksCanvas, tileMarkParts } from '../lib/tiles'
import { useSharpened } from '../lib/progressive'

/**
 * 标记区域的叠加层：把诊断标出的坐标画在图上。
 *
 * 用 SVG 而不是一堆 div：坐标沿用诊断自己的画布，缩放交给 viewBox，
 * 于是图片随窗口怎么缩放，红框都还压在同一处画面上。
 *
 * preserveAspectRatio="none" 是故意的。归一画布 1280×720 与原图 1672×941 的长宽比
 * 差 0.06%（归一时就已经这样取整了），非等比拉伸恰好把这点差异还原回去；
 * 用等比缩放反而会让红框整体偏移半格。
 */
export function TileMarks({
  boxes, canvas, className = 'marks', hover,
}: {
  boxes: readonly (readonly number[])[]
  canvas: [number, number]
  className?: string
  /**
   * 鼠标压在这一层的红上时怎么办。给了这一层才吃鼠标——默认不吃：
   * 红是叠在用户的图上的，不该挡住底下那张图（放大镜、拖动都还要用）。
   */
  hover?: {
    onPointerMove: (e: ReactPointerEvent) => void
    onPointerDown: (e: ReactPointerEvent) => void
    onPointerLeave: () => void
  }
}) {
  // 一片一片分开画，大的在前（tileMarkParts 已经按大小排好）：后画的压在上面，
  // 于是鼠标压在小片上时命中的是小片自己，提示才能贴着它说、贴着它摆
  // （2026-09-11 用户裁决：「大框应该在底下，不然小框鼠标移动过去看不到小框的提示」）。
  const parts = useMemo(() => tileMarkParts(boxes), [boxes])
  if (!parts.length) return null
  return (
    <svg
      className={className}
      viewBox={`0 0 ${canvas[0]} ${canvas[1]}`}
      preserveAspectRatio="none"
      aria-hidden
    >
      {/* 区域内部铺一层淡红、只在外轮廓描边。铺色让范围一眼可读，
          而这一层只画在用户自己那张图上，没有另一张图与它并排比颜色。 */}
      {parts.map((p, i) => (
        <g key={i}>
          <path className={hover ? 'marks-fill fmap-hit' : 'marks-fill'} d={p.fill} {...hover} />
          <path className="marks-edge" d={p.edge} />
        </g>
      ))}
    </svg>
  )
}

/**
 * 三种红各自是什么，写在鼠标底下（2026-09-11 用户裁决：「用户鼠标 hover 过去的时候，
 * tips 显示它到底是什么」）。
 *
 * 在此之前这三句摊在图下面，是一段折起来的说明：要读它得先把视线从红色上挪开、
 * 找到那行「这几种红各代表什么」、点开，再自己把第几段对回图上的哪一片。
 * 三种红同时在图上时，这个对回去的动作根本做不成。
 *
 * 数字全部来自这次诊断，一个都不写死：块数、占比、阈值、网格周期都是这张图量出来的。
 */
function tipTexts(diag: Diagnosis, windows: number): Record<'tile' | 'window' | 'lattice', string> {
  const si = diag.scale_index
  return {
    tile: `叠瓦状纹理分布：整图划分为 ${si.tiles} 个分块，检出 ${si.flagged_tiles} 块（占比 ${si.scale_tile_pct}%）。超过结构化判定阈值（${si.thresholds.structured_min_pct}%），需执行深度重绘。`,
    window: `局部高频颗粒异常，检出 ${windows} 处。`,
    lattice: `整图检出周期约 ${diag.lattice.peaks_top[0]?.period_px ?? '2 至 4'} 像素的网格纹理，高分辨率输出时可见；可通过频域滤波无损切除。`,
  }
}

/**
 * 「问题出在哪」：用户自己那张图，检出的每一项都用红色叠在上面。
 *
 * 背景：诊断三卡给的是数字（23.2%、44/190 个分块）。数字能证明结论成立，
 *   但回答不了用户真正问的那句「到底哪儿有问题」。坐标管线本来就写在产物里
 *   （叠瓦的 flagged_tile_boxes、鳞片的窗口 box），一直只当下载用的热力图存着。
 * 设计意图：2026-09-11 用户裁决「这个最好红色叠加一下」——检出什么就红什么，
 *   三项各有各的画法，因为它们本来就不是同一种东西：
 *
 *   | 检出项 | 坐标在哪 | 怎么画 |
 *   | --- | --- | --- |
 *   | 叠瓦分块 | 归一画布（measured_on） | 合并成片、画外轮廓 |
 *   | 鳞片窗口 | 原始像素 | 各画各的框（窗口本来就是分散取样的） |
 *   | 格子纹 | **没有坐标** | 整幅叠一层红 |
 *
 *   格子纹那一档不能留空。它是频域上的周期性尖峰，铺满整幅画面、没有「哪几块」
 *   可指——而这一屏若因此什么都不画，用户只会以为我们没检出。所以整幅叠红，
 *   并在图下把「它是全幅的」说出来。
 * 关键约束：
 *   1. 坐标只取自诊断记录，除了对齐取整不做任何修饰。画歪了就是在编造「问题出在哪」。
 *   2. 两套坐标各自一层 SVG。叠瓦量在归一画布上、鳞片量在原图上，
 *      塞进同一个 viewBox 必然有一套是错位的。
 *   3. 三项全未检出时整块不渲染。空图配一句「问题出在哪」等于自己承认在凑版式。
 *   4. 整幅是缩放显示的，红色范围里的纹理在这个尺寸下**看不见**。这一点必须写在图下，
 *      否则用户会盯着红色找纹理，找不到就认为标记是假的。
 *   5. 只在 hover 时才出来的东西，图下要有一句话说它在（见 tipTexts）。
 *      没人告诉的悬停等于不存在，触屏上更是压根没有 hover——所以按一下也给。
 */
export function ProblemMap({ diag, src, soft }: {
  diag: Diagnosis
  src: string
  /**
   * 同尺寸的模糊占位图。给了就先摆它，原图到了再无声换掉（见 useSharpened）：
   * 原图跨境要几十秒，而这一屏一开就把红框画在了一张只到三分之一的图上。
   */
  soft?: string | null
}) {
  const shown = useSharpened(src, soft)
  const frame = useRef<HTMLDivElement>(null)
  const tipEl = useRef<HTMLSpanElement>(null)
  const [tip, setTip] = useState<{ kind: 'tile' | 'window' | 'lattice'; x: number; y: number } | null>(null)
  const si = diag.scale_index
  const tiles = si.flagged_tile_boxes ?? []
  // 只画管线自己标了 flagged 的窗口。取样窗口本身不是问题，画上去等于把
  // 「我们看过这里」说成「这里有问题」。
  const windows = [...diag.granule.flat_windows, ...diag.granule.oriented_windows]
    .filter(w => w.flagged)
    .map(w => w.box)
  const lattice = diag.lattice.detected

  if (!tiles.length && !windows.length && !lattice) return null

  const canvas = marksCanvas(si.measured_on, diag.pixels)
  const what = [
    tiles.length && '叠瓦状纹理',
    windows.length && '局部颗粒噪点',
    lattice && '全幅周期网格',
  ].filter(Boolean).join('、')

  const lead = [
    !!tiles.length && `叠瓦状纹理占比 ${si.scale_tile_pct}%（${si.flagged_tiles}/${si.tiles} 块）`,
    !!windows.length && `${windows.length} 处颗粒异常`,
    lattice && '全幅网格纹理',
  ].filter(Boolean).join('、')

  const texts = tipTexts(diag, windows.length)
  /**
   * 说明摆在哪。
   *
   * 摆在**那一片红的正上方**，不是摆在鼠标正上方：鼠标压着的就是那一片，说明再压上去
   * 就把要看的东西盖住了（2026-09-11 用户裁决：「小格子应该在上面，不然被遮挡了」）。
   * 一片红太大时（比如铺满半张图的连片，还有全幅那层斜纹）没有「上方」可言，那就回到
   * 跟着鼠标走。
   *
   * 上下左右都收在框里：这个框是 overflow: hidden 的，说明只要探出去一点就被齐齐切掉，
   * 而人看到的是「移上去什么都没有」——正是它先被报上来的那个样子。
   * 高度按已经量到的那一版算（说明一直挂着，鼠标一动就会再算一次）。
   */
  const hover = (kind: 'tile' | 'window' | 'lattice', anchor: 'shape' | 'cursor' = 'shape') => {
    const at = (e: ReactPointerEvent) => {
      const r = frame.current?.getBoundingClientRect()
      if (!r) return
      const h = tipEl.current?.offsetHeight ?? 96
      const half = Math.min((tipEl.current?.offsetWidth ?? 300) / 2 + 8, r.width / 2)
      const b = (e.currentTarget as Element).getBoundingClientRect()
      const onShape = anchor === 'shape' && b.height < r.height * 0.5 && b.width < r.width * 0.6
      const cx = onShape ? b.left + b.width / 2 - r.left : e.clientX - r.left
      const above = (onShape ? b.top - r.top : e.clientY - r.top) - 14 - h
      const below = (onShape ? b.bottom - r.top : e.clientY - r.top) + 14
      let y = above >= 6 ? above : below
      if (y + h > r.height - 6) y = Math.max(6, r.height - h - 6)
      setTip({ kind, x: Math.min(Math.max(cx, half), r.width - half), y })
    }
    // 触屏上没有 hover，按一下也给（那时它就是「点哪儿说哪儿」）。
    return { onPointerMove: at, onPointerDown: at, onPointerLeave: () => setTip(null) }
  }

  return (
    <figure className="fmap">
      <div className="fmap-frame" ref={frame}>
        <img src={shown.src} alt={`图像异常检测标注：${what}`} />
        {/* 全幅那一层铺在最下面：它没有具体位置，盖住分块的红框会让人以为那些框也是虚的。
            它也是兜底的那一层——鼠标落在没有分块、没有窗口的地方，说的就是格子纹。 */}
        {lattice && (
          <>
            <span className="marks fmap-wash fmap-hit" {...hover('lattice', 'cursor')} />
            <span className="fmap-chip">格子纹 · 全幅</span>
          </>
        )}
        {!!tiles.length && (
          <TileMarks boxes={tiles} canvas={canvas} className="marks fmap-marks"
                     hover={hover('tile')} />
        )}
        {!!windows.length && (
          <TileMarks boxes={windows} canvas={diag.pixels} className="marks fmap-marks"
                     hover={hover('window')} />
        )}
        {shown.soft && (
          <span className="fmap-load"><IconSpinner size={12} />高清原图加载中</span>
        )}
        {tip && (
          <span className="fmap-tip" ref={tipEl}
                style={{ left: `${tip.x}px`, top: `${tip.y}px` }} role="status">
            {texts[tip.kind]}
          </span>
        )}
      </div>
      <figcaption>
        <IconInfo size={15} />
        <span>
          {'异常检测标注区域：'}<b>{lead}</b>
          {'。'}
          <em>
            {'微结构纹理仅占数个像素，缩放视图下不易察觉。悬停或点击标注区域可查看对应分析。'}
          </em>
        </span>
      </figcaption>
    </figure>
  )
}
